/**
 * Шапка блока тем (#845 PR 6): охват («темы присвоены в N из A разговоров» и,
 * если есть, «в D определить не удалось» — и при нуле присвоенных, иначе сбой
 * шага не виден), строка «Без темы» — мера годности набора, видна всегда, пока
 * есть присвоенные темы, — и, для тех, кто правит набор, число предложенных тем.
 */
import { useTranslation } from 'react-i18next';
import type { VoiceThemes } from '@aica/api-types/schemas';
import { CONTROL_MIN_HEIGHT } from '@/components/ui/control-size';
import { cn } from '@/lib/utils';
import { VoiceRowCard } from './VoiceRowCard';
import type { VoiceRowDetailsTrend } from './voice-series-view-model';
import { noThemeRow, themeCoverageLines } from './voice-themes-view-model';

interface VoiceThemesHeaderProps {
  themes: VoiceThemes;
  expandedRow: string | undefined;
  onToggleRow: (rowId: string) => void;
  onOpenCall: (recordId: string) => void;
  /** Предложения новых тем; `null` — строки нет (не правит набор или нечего). */
  proposalCount: number | null;
  onOpenThemes?: () => void;
  /** The «no theme» row's series (#2394 §9 п. 7), asked with the block's. */
  noThemeTrend?: VoiceRowDetailsTrend;
}

export function VoiceThemesHeader({
  themes,
  expandedRow,
  onToggleRow,
  onOpenCall,
  proposalCount,
  onOpenThemes,
  noThemeTrend,
}: VoiceThemesHeaderProps) {
  const { t } = useTranslation('dashboard');
  const assigned = themes.coverage.assignedCalls > 0;
  const row = noThemeRow(themes);
  const showProposals = proposalCount !== null && proposalCount > 0 && onOpenThemes;
  // The step ran and could not assign: the count is said even with nothing
  // assigned — «not assigned» alone would hide that the step failed.
  const showCoverage = assigned || themes.coverage.degradedCalls > 0;
  if (!showCoverage && !showProposals) return null;
  return (
    <div className="mb-3 space-y-3">
      {showCoverage && (
        <div
          className="space-y-1 text-xs text-fg-muted"
          data-testid="voice-themes-coverage"
        >
          {themeCoverageLines(themes, t).map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      )}
      {assigned && (
        <div className="space-y-1">
          <VoiceRowCard
            row={row}
            expanded={expandedRow === row.id}
            onToggle={() => onToggleRow(row.id)}
            onOpenCall={onOpenCall}
            trend={noThemeTrend}
          />
          <p className="text-xs text-fg-subtle">
            {t('voicePage.themes.analytics.noThemeHint')}
          </p>
        </div>
      )}
      {showProposals && (
        <button
          type="button"
          className={cn(
            CONTROL_MIN_HEIGHT.sm,
            'inline-flex items-center text-left text-sm font-medium text-primary transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
          onClick={onOpenThemes}
        >
          {t('voicePage.themes.analytics.proposals', { count: proposalCount ?? 0 })}
        </button>
      )}
    </div>
  );
}
